import { Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom'
import { lazy, Suspense, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { ThemeProvider } from '@/components/theme/ThemeProvider'
import Footer from '@/components/Footer'
import FloatingCTA from '@/components/FloatingCTA'
import KonamiCodeHandler from '@/components/KonamiCodeHandler'
import CustomCursor from '@/components/interactive/CustomCursor'
import HomePage from '@/routes/HomePage'
import { Analytics } from '@vercel/analytics/react'

const CardPage = lazy(() => import('@/routes/CardPage'))
const BeyondCodePage = lazy(() => import('@/routes/BeyondCodePage'))
const BlogPage = lazy(() => import('@/routes/BlogPage'))
const BlogPostPage = lazy(() => import('@/routes/BlogPostPage'))
const CoachingPage = lazy(() => import('@/routes/CoachingPage'))
const LoopBreakerPage = lazy(() => import('@/routes/LoopBreakerPage'))

const SUPPORTED_LOCALES = ['en', 'zh-TW', 'zh-CN'] as const
type Locale = typeof SUPPORTED_LOCALES[number]

function LocaleWrapper({ children }: { children: React.ReactNode }) {
  const { locale } = useParams<{ locale: string }>()
  const navigate = useNavigate()
  const { i18n } = useTranslation()

  useEffect(() => {
    if (locale && SUPPORTED_LOCALES.includes(locale as Locale)) {
      if (i18n.language !== locale) {
        i18n.changeLanguage(locale)
      }
    } else {
      navigate('/en', { replace: true })
    }
  }, [locale, i18n, navigate])

  return (
    <>
      <KonamiCodeHandler />
      {children}
    </>
  )
}

function App() {
  return (
    <ThemeProvider>
      <Helmet>
        <title>Nicholas's Personal Website</title>
        <meta name="description" content="Nicholas's Personal Website with resume, portfolio, and contact information" />
      </Helmet>

      <Routes>
        <Route path="/" element={<Navigate to="/en" replace />} />

        <Route path="/:locale" element={
          <LocaleWrapper>
            <HomePage />
            <Footer />
            <FloatingCTA />
          </LocaleWrapper>
        } />

        <Route path="/:locale/card" element={
          <LocaleWrapper>
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
              <CardPage />
            </Suspense>
          </LocaleWrapper>
        } />

        <Route path="/:locale/blog" element={
          <LocaleWrapper>
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
              <BlogPage />
            </Suspense>
            <Footer />
          </LocaleWrapper>
        } />

        <Route path="/:locale/blog/:slug" element={
          <LocaleWrapper>
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
              <BlogPostPage />
            </Suspense>
            <Footer />
          </LocaleWrapper>
        } />

        <Route path="/:locale/coaching" element={
          <LocaleWrapper>
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
              <CoachingPage />
            </Suspense>
            <Footer />
          </LocaleWrapper>
        } />

        <Route path="/:locale/loop-breaker" element={
          <LocaleWrapper>
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
              <LoopBreakerPage />
            </Suspense>
          </LocaleWrapper>
        } />

        <Route path="/:locale/beyond-code" element={
          <LocaleWrapper>
            <Suspense fallback={<div className="min-h-screen bg-background" />}>
              <BeyondCodePage />
            </Suspense>
          </LocaleWrapper>
        } />

        <Route path="*" element={<Navigate to="/en" replace />} />
      </Routes>

      <CustomCursor />
      <Analytics />
    </ThemeProvider>
  )
}

export default App
